import { useState } from 'react';
import type { EmployeeCapabilitiesResponse, EmployeeCapabilityComputedResponse, EmployeeProfileResponse } from '../../api/types';
import { useApiResource } from '../../hooks/useApiResource';
import { FactSection } from './FactSection';
import listStyles from './FactRecords.module.css';
import factStyles from './CaptureSection.module.css';

const COMPONENT_TYPE_LABEL: Record<string, string> = {
  attribute: 'Attribute',
  skill: 'Skill',
  experience_area: 'Experience area',
};

/**
 * The Capability section (Mission 166, implementing the specification
 * `docs/DOMAIN_DECISIONS.md`'s "Composite Concepts" (Mission 165)
 * authorised, Part 2G).
 *
 * ---------------------------------------------------------------------
 * A CAPABILITY IS COMPUTED, NEVER RECORDED HERE
 *
 * Unlike Traits and Talent Designations, a Capability is Derived (Mission
 * 165 Part 1's boundary matrix): nobody types in a Capability value, and
 * this section offers no form. It is the same relationship Role Fit has to
 * its own inputs — computed fresh on every read from whichever components
 * currently have a known value — so this section is read-only, the same
 * way `RoleFitSection` is.
 *
 * NEVER A SINGLE "EMPLOYEE SCORE"
 *
 * Each Capability is its own named figure with its own breakdown. There is
 * no total, no average-of-capabilities, and no ranking across them — Mission
 * 166 is explicit that Capability must never collapse into a generic score
 * or hidden ranking mechanism. The breakdown discloses exactly what
 * `RoleFitBreakdown` discloses for a role requirement: which components
 * were actually used, which were excluded and why, and the confidence the
 * figure carries — never a bare number with nothing behind it.
 *
 * SENSITIVITY IS SERVER-DECIDED
 *
 * An Elevated-classified Capability this viewer cannot see is simply
 * absent from the response (never a 403 on this collection), mirroring
 * every other sensitivity-gated collection in this application. This
 * section has no separate handling for that — there is nothing to handle.
 * ---------------------------------------------------------------------
 */
export function CapabilitiesSection({ profile }: { profile: EmployeeProfileResponse }) {
  const capabilities = useApiResource<EmployeeCapabilitiesResponse>(`/employees/${profile.id}/capabilities`);

  return (
    <FactSection
      id="capabilities"
      title="Capabilities"
      state={capabilities}
      loadingLabel="Loading capabilities…"
      errorTitle="Capabilities could not be loaded"
      isEmpty={(data) => data.capabilities.length === 0}
      empty={{
        title: 'No capabilities defined',
        detail: 'No capability in the library currently applies here. That is a statement about the library, not about this person.',
      }}
      note="Each Capability is a weighted composite computed fresh from Attribute, Skill and Experience Area records currently on file — never a rating anyone entered directly, and never a single overall score."
    >
      {(data) => (
        <ul className={listStyles.list}>
          {data.capabilities.map((capability) => (
            <CapabilityCard key={capability.capabilityDefinitionId} capability={capability} />
          ))}
        </ul>
      )}
    </FactSection>
  );
}

function CapabilityCard({ capability }: { capability: EmployeeCapabilityComputedResponse }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <li className={listStyles.record}>
      <div className={listStyles.primary}>
        <span className={listStyles.name}>{capability.name}</span>
        {capability.status === 'computed' ? (
          <span className={listStyles.marker}>
            {capability.value} of 20 · {capability.confidence} confidence
          </span>
        ) : (
          <span className={listStyles.marker}>Not currently computable</span>
        )}
        <button type="button" className={factStyles.editToggle} onClick={() => setExpanded((open) => !open)}>
          {expanded ? 'Hide breakdown' : 'Show breakdown'}
        </button>
      </div>

      {capability.status === 'not_evaluated' ? (
        <p className={listStyles.notes}>
          None of this capability's components currently has a known value for this person — an absence of data,
          never a fabricated result.
        </p>
      ) : null}

      {expanded ? (
        <dl className={listStyles.facts}>
          {capability.components.map((component) => (
            <div key={component.componentId}>
              <dt>
                {component.subjectName} ({COMPONENT_TYPE_LABEL[component.subjectType] ?? component.subjectType})
              </dt>
              <dd>
                {component.includedInComputation
                  ? `${(component.normalizedWeight! * 100).toFixed(0)}% of the weight actually used · ${component.confidence} confidence`
                  : 'Excluded — nothing currently known for this component'}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
    </li>
  );
}
